import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';

import { downloadFlattenedPdf } from 'common/PSPDFKit/downloadFlattenedPdf';
import { useDocumentAnnotationsSource } from 'hooks/document-annotations-hooks';
import { useDocumentRotationSource } from 'hooks/document-rotation-hooks';
import { AlertMessage } from 'utilities/utils';

import AnnotationVisibilityNotice from 'components/UploadedFiles/AnnotationVisibilityNotice';
import DownloadOptionsDialog from 'components/UploadedFiles/DownloadOptionsDialog';
import {
  DOWNLOAD_REQUESTED_EVENT,
  DownloadRequestedDetail,
} from 'common/PSPDFKit/downloadEvents';

type DocumentPDFViewerProps = {
  url: string;
  fileName: string;
  /** Pre-signed attachment URL for "download original". See PSPDFViewer's
   * prop doc — this is distinct from `url`, which is the inline-viewer URL. */
  downloadUrl?: string;
  documentUuid?: string | null;
  /** True while an optimistic rename/accept mutation is in flight. Greys the
   * PSPDFKit Download button and short-circuits any DOWNLOAD_REQUESTED_EVENT
   * that races through, so the user can't download a file whose
   * Content-Disposition name hasn't been committed server-side yet. */
  isRenaming?: boolean;
};

const wrapperStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: 'inherit',
};

const viewerSlotStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  // Flex-1 would be enough for PSPDFKit's own mounting, but the wrapped
  // PSPDFViewer's container uses `height: inherit`, which reads the parent's
  // declared `height` property value — not the flex-computed height. Setting
  // height: '100%' here gives `inherit` something concrete to read.
  height: '100%',
};

const PSPDFViewer = lazy(() => import('common/PSPDFKit/PSPDFViewer'));

const pdfViewerFallbackStyle: React.CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
};

const pdfViewerFallback = (
  <div
    role="status"
    aria-label="Loading PDF viewer"
    style={pdfViewerFallbackStyle}
  >
    Loading PDF viewer...
  </div>
);
const pdfViewerErrorFallback = (
  <div role="alert" style={pdfViewerFallbackStyle}>
    Unable to load PDF viewer. Please refresh and try again.
  </div>
);

/** Resolve truthy/falsy for "has annotations" via PSPDFKit's Instant JSON. */
const hasAnnotations = async (): Promise<boolean> => {
  const instance = window.PSPDFKIT_INSTANCE;
  if (!instance) return false;
  try {
    const exported = (await instance.exportInstantJSON()) as {
      annotations?: unknown[];
    };
    return (exported?.annotations?.length ?? 0) > 0;
  } catch {
    // If we can't inspect the current state, treat as no annotations — safer
    // to skip the dialog than to block the user with a misleading prompt.
    return false;
  }
};

/**
 * Trigger a browser download of the pre-signed attachment URL. Relies on the
 * server setting `Content-Disposition: attachment` (olympusapi's
 * `download_url` helper does this); the `download` attribute is advisory and
 * is ignored cross-origin, so the server header is what actually carries.
 *
 * Using an anchor click instead of `window.location.href = url` declares the
 * download intent at the HTML layer and keeps the flow symmetric with the
 * flatten path in `downloadFlattenedPdf`.
 */
const downloadOriginal = (originalUrl: string): void => {
  const a = document.createElement('a');
  a.href = originalUrl;
  a.rel = 'noopener';
  a.style.display = 'none';
  a.download = '';
  document.body.appendChild(a);
  try {
    a.click();
  } finally {
    // Deferred cleanup for parity with the flatten path; browsers have been
    // known to cancel downloads if the anchor is removed synchronously.
    window.setTimeout(() => {
      document.body.removeChild(a);
    }, 0);
  }
};

/**
 * Thin wrapper around PSPDFViewer that wires server-side annotation persistence
 * when a document UUID is available. Falls back to in-memory-only behavior if
 * the UUID hasn't loaded yet.
 *
 * Also hosts:
 *  - the one-time firm-only visibility notice shown the first time the user
 *    creates an annotation;
 *  - the download-options dialog that lets the user pick between "download
 *    with annotations" (flattened) and "download original" when a PDF has
 *    any annotations. Empty documents download the original directly with
 *    no prompt.
 */
export default function DocumentPDFViewer({
  url,
  fileName,
  downloadUrl,
  documentUuid,
  isRenaming = false,
}: DocumentPDFViewerProps) {
  const annotationsSource = useDocumentAnnotationsSource(documentUuid);
  const rotationSource = useDocumentRotationSource(documentUuid);

  const [showNotice, setShowNotice] = useState(false);
  const [downloadDialogUrl, setDownloadDialogUrl] = useState<string | null>(
    null,
  );

  // Belt-and-braces for the Download disable: the PSPDFKit toolbar item is
  // greyed via `disableDownload` below, but the DOWNLOAD_REQUESTED_EVENT
  // listener also reads this ref so any in-flight click that slipped through
  // (e.g. fired before the toolbar repaints) becomes a no-op rather than
  // downloading a file with a stale Content-Disposition name.
  const isRenamingRef = useRef(isRenaming);
  isRenamingRef.current = isRenaming;

  const handleFirstAnnotationCreate = useCallback(() => {
    setShowNotice(true);
  }, []);

  const handleDismissNotice = useCallback(() => {
    setShowNotice(false);
  }, []);

  useEffect(() => {
    const handleDownloadRequest = async (e: Event): Promise<void> => {
      if (isRenamingRef.current) return;
      const { detail } = e as CustomEvent<DownloadRequestedDetail>;
      if (!detail?.originalUrl) return;

      if (await hasAnnotations()) {
        setDownloadDialogUrl(detail.originalUrl);
      } else {
        downloadOriginal(detail.originalUrl);
      }
    };

    window.addEventListener(
      DOWNLOAD_REQUESTED_EVENT,
      handleDownloadRequest as EventListener,
    );
    return () => {
      window.removeEventListener(
        DOWNLOAD_REQUESTED_EVENT,
        handleDownloadRequest as EventListener,
      );
    };
  }, []);

  const closeDownloadDialog = useCallback(() => {
    setDownloadDialogUrl(null);
  }, []);

  const handleDownloadWithAnnotations = useCallback(() => {
    const instance = window.PSPDFKIT_INSTANCE;
    closeDownloadDialog();
    if (!instance) return;
    downloadFlattenedPdf(instance).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[DocumentPDFViewer] flatten failed', err);
      AlertMessage(
        'error',
        'Could not prepare the annotated download. Please try again.',
        3000,
      );
    });
  }, [closeDownloadDialog]);

  const handleDownloadOriginal = useCallback(() => {
    closeDownloadDialog();
    if (downloadDialogUrl) downloadOriginal(downloadDialogUrl);
  }, [closeDownloadDialog, downloadDialogUrl]);

  return (
    <div style={wrapperStyle}>
      <AnnotationVisibilityNotice
        visible={showNotice}
        onDismiss={handleDismissNotice}
      />
      <div style={viewerSlotStyle}>
        <Sentry.ErrorBoundary
          key={url}
          fallback={pdfViewerErrorFallback}
          beforeCapture={(scope) => {
            scope.setTag('component', 'PDFViewerLoader');
            scope.setTag('operation', 'loadPSPDFViewer');
          }}
        >
          <Suspense fallback={pdfViewerFallback}>
            <PSPDFViewer
              url={url}
              fileName={fileName}
              downloadUrl={downloadUrl}
              disableDownload={isRenaming}
              annotationsSource={annotationsSource}
              rotationSource={rotationSource}
              onFirstAnnotationCreate={handleFirstAnnotationCreate}
            />
          </Suspense>
        </Sentry.ErrorBoundary>
      </div>
      <DownloadOptionsDialog
        isOpen={downloadDialogUrl !== null}
        onClose={closeDownloadDialog}
        onDownloadWithAnnotations={handleDownloadWithAnnotations}
        onDownloadOriginal={handleDownloadOriginal}
      />
    </div>
  );
}
